/**
 * A History breakdown card (By venue / By league / By model): net P&L, ROI, record (W-L-P) and
 * staked per group, best net first. Desktop shows it open; on mobile it's a collapsible card.
 * When `onPick` is given, a row toggles the bets-list filter for that group.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Skeleton } from "../ui.js";
import { ErrorState } from "../ui.js";
import { fmtRoi } from "../../lib/format.js";
import { breakdownRows, fmtAmt, fmtSignedAmt, record, type Book, type GroupLike } from "../../lib/history.js";

const TOP = 5;

export function Breakdown({
  title,
  groups,
  ccy,
  loading,
  error,
  onRetry,
  collapsible,
  activeKey,
  onPick,
  iconFor,
  pickHint,
}: {
  title: string;
  groups: GroupLike[];
  ccy: Book;
  loading: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Mobile: a collapsed card the user opens. */
  collapsible: boolean;
  activeKey?: string | null;
  onPick?: (key: string) => void;
  iconFor?: (g: GroupLike) => ReactNode;
  pickHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const sorted = useMemo(() => breakdownRows(groups), [groups]);
  const shown = all ? sorted : sorted.slice(0, TOP);
  const expanded = !collapsible || open;
  const bodyId = `bd-${title.replace(/\W+/g, "-").toLowerCase()}`;

  const header = (
    <>
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-dim">{title}</h3>
      {!loading && <span className="nums text-[11px] text-faint">{sorted.length}</span>}
      {collapsible && (
        <span className={`ml-auto text-[11px] text-faint transition-transform duration-150 motion-reduce:transition-none ${open ? "rotate-90" : ""}`} aria-hidden>
          ▸
        </span>
      )}
    </>
  );

  return (
    <section className="rounded-lg border border-border bg-panel-2" aria-label={title}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex min-h-11 w-full items-center gap-2 px-3 text-left"
        >
          {header}
        </button>
      ) : (
        <div className="flex items-center gap-2 px-3 pb-1 pt-3">{header}</div>
      )}
      {expanded && (
        <div id={bodyId} className="px-1.5 pb-2">
          {loading ? (
            <ul className="flex flex-col">
              {[0, 1, 2].map((i) => (
                <li key={i} className="px-1.5 py-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="mt-1 h-3 w-2/3" />
                </li>
              ))}
            </ul>
          ) : error ? (
            <ErrorState error={error} onRetry={onRetry} />
          ) : sorted.length === 0 ? (
            <p className="px-1.5 py-2 text-[12px] text-faint">Nothing settled in this range.</p>
          ) : (
            <>
              {onPick && pickHint && <p className="px-1.5 pb-1 text-[11px] text-faint">{pickHint}</p>}
              <ul className="flex flex-col">
                {shown.map((g) => {
                  const active = activeKey === g.key;
                  const net = g.net_pl ?? 0;
                  const inner = (
                    <>
                      <div className="flex min-w-0 items-center gap-2">
                        {iconFor?.(g)}
                        <span className={`min-w-0 flex-1 truncate text-[13px] ${active ? "text-amber" : "text-ink"}`}>{g.label ?? g.key}</span>
                        <span className={`nums shrink-0 text-[13px] font-medium ${net >= 0.005 ? "text-up" : net <= -0.005 ? "text-down" : "text-dim"}`}>
                          {fmtSignedAmt(net, ccy)}
                        </span>
                      </div>
                      <div className="nums mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-faint">
                        <span>ROI {fmtRoi(g.roi)}</span>
                        <span>· {record(g)}</span>
                        <span>· {fmtAmt(g.staked ?? 0, ccy)} staked</span>
                      </div>
                    </>
                  );
                  return (
                    <li key={g.key}>
                      {onPick ? (
                        <button
                          type="button"
                          onClick={() => onPick(g.key)}
                          aria-pressed={active}
                          className={`block min-h-11 w-full rounded-md border px-1.5 py-2 text-left transition-colors duration-150 motion-reduce:transition-none lg:min-h-0 ${
                            active ? "border-amber/50 bg-amber/10" : "border-transparent hover:bg-raised"
                          }`}
                        >
                          {inner}
                        </button>
                      ) : (
                        <div className="px-1.5 py-2">{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {sorted.length > TOP && (
                <button
                  type="button"
                  onClick={() => setAll((v) => !v)}
                  className="mx-1.5 mt-1 min-h-11 text-[12px] text-dim hover:text-amber lg:min-h-0"
                >
                  {all ? "Show fewer" : `Show all ${sorted.length}`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
